import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { Card, Flex, Grid, GridItem } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { TipoModulo } from "@/services/aux-entities.service";
import { useRoles } from "@/hooks/useRoles";
import {
  fetchModuleTypesHandler,
  handleCreateTruckHandler,
} from "../schema/handler-functions";
import { AppColors } from "@/styles/colors";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { truckCreationSchema } from "../schema/trucks.schema";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import {
  onlyUppercaseAlphanumeric,
  argentinianPlate,
  onlyAlphanumericAndSpaces,
} from "@/utils/reg-expressions";
import { isEmpty } from "@/utils/query-string";

export const TruckCreationPage = () => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();

  const handleCancel = () => {
    navigate(AppRoutes.TRUCKS);
  };

  const { control, getValues, setError, clearErrors } = useForm({
    resolver: zodResolver(truckCreationSchema),
  });
  const { fetchPermissions } = useRoles();

  const [moduleTypes, setModuleTypes] = useState<TipoModulo[] | null>([]);
  const [loadingModuleTypes, setLoadingModuleTypes] = useState(false);
  const [loadingCreateTruck, setLoadingCreateTruck] = useState(false);

  const fetchModuleTypes = async () => {
    await fetchModuleTypesHandler({
      setLoadingModuleTypes,
      setModuleTypes,
      fetchPermissions,
      navigate,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchModuleTypes();
    }
  }, [isActive]);

  const handleAdd = async () => {
    handleCreateTruckHandler({
      fetchPermissions,
      getValues,
      navigate,
      setLoadingCreateTruck,
    });
  };

  const watchedValues = useWatch({
    control,
  });

  useEffect(() => {
    if (!watchedValues.patente) return clearErrors("patente");
    if (argentinianPlate.test(watchedValues.patente)) {
      clearErrors("patente");
    } else {
      setError("patente", {
        type: "pattern",
        message: "La patente debe tener el formato estándar: AAA000 o AA000AA",
      });
    }
  }, [watchedValues.patente]);

    const hasNoFilledValues = useMemo(() => {
      const { nombre, patente, modulos } = watchedValues || {};
  
      return !nombre || isEmpty(patente) || isEmpty(modulos) || !argentinianPlate.test(watchedValues.patente)
    }, [watchedValues]);
  

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="trucks-creation-page">
      <SectionHeader
        title="Maestro de camiones"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleAdd={handleAdd}
        handleCancel={handleCancel}
        loading={loadingCreateTruck}
        disableAdd={hasNoFilledValues || loadingCreateTruck}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)" gap="16px">
            <GridItem>
              <InputField
                control={control}
                inputName="nombre"
                labelText="Nombre"
                placeholder="Ingresar nombre"
                required
                allowedPattern={onlyAlphanumericAndSpaces}
                maxLength={20}
              />
            </GridItem>
            <GridItem>
              <InputField
                control={control}
                inputName="patente"
                labelText="Patente"
                placeholder="Ingresar patente"
                required
                allowedPattern={onlyUppercaseAlphanumeric}
                maxLength={7}
              />
            </GridItem>
            <GridItem>
              <SelectComponent
                control={control}
                name="modulos"
                items={
                  moduleTypes?.map((item) => ({
                    label: item.descripcion,
                    value: item.id,
                  })) ?? []
                }
                loading={loadingModuleTypes}
                disabled={loadingModuleTypes}
                placeholder="Seleccionar tipo de modulo"
                clearable
                required
                multiple
                labelText="Tipo de pedido"
                minHeight="40px"
              />
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>
    </Flex>
  );
};
